
import React from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '@/components/layout/PageLayout';
import ScrollReveal from '@/components/ui/ScrollReveal';
import { cities, industries, cityIndustryMap } from '@/data/cityIndustryData';

const WebDesignDirectories: React.FC = () => {
  const schemaData = [{
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Web Design Services Directory",
    "description": "Find specialized web design services for various industries and cities near Baton Rouge, LA.",
    "publisher": {
      "@type": "Organization",
      "name": "Astrem"
    }
  }];

  return (
    <PageLayout 
      title="Web Design Services Directory | Industry & City Specific Services" 
      description="Find specialized web design services for your industry and location. We serve businesses in Denham Springs, Gonzales, Prairieville, and more cities around Baton Rouge."
      ogImage="https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/astrem_og_Websites%20Content%20Marketing%20Solutions.jpg"
      schemaData={schemaData}
    >
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-medium mb-6">
                Industry & City Web Design Services
              </h1>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Find specialized web design services for your specific industry and location. We understand the unique needs of businesses in each area we serve.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-display font-medium">
                Web Design by City
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto mt-4">
                We provide specialized web design services to businesses in these cities around Baton Rouge.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {Object.entries(cities).map(([slug, city], index) => (
              <ScrollReveal key={slug} delay={index * 50}>
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                  <h3 className="text-xl font-medium mb-3">{city.name}, LA</h3>
                  <p className="text-gray-600 mb-4 line-clamp-2">{city.description}</p>
                  <h4 className="font-medium mb-2">Industries We Serve:</h4>
                  <ul className="space-y-1">
                    {(cityIndustryMap[slug] || []).slice(0, 5).map((industrySlug) => (
                      <li key={industrySlug}>
                        <Link 
                          to={`/web-design/${industrySlug}/${slug}`}
                          className="text-blue-600 hover:underline flex items-center"
                        >
                          {industries[industrySlug]?.name || "Industry"}
                        </Link>
                      </li>
                    ))}
                    {(cityIndustryMap[slug] || []).length > 5 && (
                      <li className="text-gray-500">+ {(cityIndustryMap[slug] || []).length - 5} more industries</li>
                    )}
                  </ul>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
      
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-display font-medium">
                Web Design by Industry
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto mt-4">
                We create specialized websites tailored to the unique needs of these industries.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {Object.entries(industries).map(([slug, industry], index) => {
              // Find cities that serve this industry
              const servingCities = Object.entries(cityIndustryMap)
                .filter(([_, industryList]) => industryList.includes(slug))
                .map(([citySlug]) => citySlug);
              
              return (
                <ScrollReveal key={slug} delay={index % 10 * 30}>
                  <div className="bg-white rounded-lg p-4 border border-gray-200 hover:border-blue-300 transition-all">
                    <h3 className="font-medium mb-2">{industry.name}</h3>
                    {servingCities.length > 0 && (
                      <Link 
                        to={`/web-design/${slug}/${servingCities[0]}`}
                        className="text-blue-600 hover:underline text-sm"
                      >
                        View in {cities[servingCities[0]]?.name || "a city"}
                      </Link>
                    )}
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl font-display font-medium mb-6">
                All Industry-Specific Web Design Services
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Browse our complete list of industry-specific web design landing pages. Each page contains tailored content and solutions for the specific industry and location.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-8">
                {Object.entries(cityIndustryMap).flatMap(([citySlug, industryList]) => 
                  industryList.map(industrySlug => {
                    const city = cities[citySlug];
                    const industry = industries[industrySlug];
                    if (!city || !industry) return null;
                    
                    return (
                      <Link 
                        key={`${industrySlug}-${citySlug}`}
                        to={`/web-design/${industrySlug}/${citySlug}`}
                        className="text-blue-600 hover:underline py-1 border-b border-gray-100"
                      >
                        {industry.name} Web Design in {city.name}, LA
                      </Link>
                    );
                  })
                ).filter(Boolean)}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </PageLayout>
  );
};

export default WebDesignDirectories;
